import { Theme } from '@nykaa/ui-components/themes/types';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import {
  ListingProduct,
  TipTile,
  isListingItemTipTile,
} from 'store/categoryListing/types';
import {
  PRO,
  PRODUCT_CARD_CTA_REVAMP,
  PRODUCT_CARD_DEFAULT,
  PRODUCT_CARD_V2,
  PRODUCT_CARD_V3,
  SITE_NAVIGATION,
  SITE_SUB_NAVIGATION,
} from './constants';
import { isValidObject } from '@nykaa/utils/validations';
import { plpInteractions } from '@nykaa/data-layer/constants';
import { TrackingForAppParams } from 'layouts/ConversationalSearch/types';

const defaultWidth = 400; //common phone width
const dafaultCardHeight = 300; // product card height 300 set already
const maxCardHeight = 250;
const defaultReviewHeight = 12;
const defaultCtaHeight = 36;
const defaultImageHeight = 156;
const NO_TAG = 'no tag';

const calculateMaxWidth = () => {
  let width = defaultWidth / 2;
  if (typeof window !== undefined) {
    width = (global?.window?.innerWidth || defaultWidth) / 2 - 2;
  }
  return width;
};

export const calculateHeight = (
  imageAspectRatio: number,
  reviewRatingVisible: boolean,
  ctaVisible: boolean,
  hasDynamicUi: boolean
) => {
  let cardHeight = defaultImageHeight;
  let width = defaultWidth / 2;
  let totalHeight = dafaultCardHeight;
  const maxHeight = maxCardHeight;
  if (hasDynamicUi) {
    const reviewHeight = reviewRatingVisible ? 0 : defaultReviewHeight;
    const ctaHeight = ctaVisible ? 0 : defaultCtaHeight;
    const imageHeight = defaultImageHeight;
    width = calculateMaxWidth();
    cardHeight = width / imageAspectRatio;
    cardHeight = cardHeight > maxHeight ? maxHeight : cardHeight;
    // substract all height from default card height 300 for new height
    totalHeight =
      totalHeight -
      reviewHeight -
      ctaHeight +
      Math.abs(imageHeight - cardHeight);
  }
  return { totalHeight, cardHeight, width, maxHeight };
};

export const getTagsData = (product: ListingProduct) => {
  if (!product || !product?.dynamicTags) {
    return '';
  }
  let oldTags = NO_TAG;
  const dynamicTags = product?.dynamicTags?.[0];
  if (typeof dynamicTags === 'string' && dynamicTags) {
    oldTags = dynamicTags;
  } else if (typeof dynamicTags === 'object' && dynamicTags?.id) {
    oldTags = dynamicTags.id;
  }
  let newTags: string | string[] = '';
  if (product && Array.isArray(product?.newTags)) {
    newTags = product.newTags.map((tag) => tag?.title) || [];
    newTags = newTags?.join(',') || NO_TAG;
  }
  const tags = `${newTags} | ${oldTags}`;
  return tags;
};

// Product Card V3 helpers
export const getProductCardVersion = (
  v2: boolean,
  v3: boolean,
  newCta: boolean
): string => {
  if (newCta) {
    return PRODUCT_CARD_CTA_REVAMP;
  } else if (v2) {
    return PRODUCT_CARD_V2;
  } else if (v3) {
    return PRODUCT_CARD_V3;
  } else {
    return PRODUCT_CARD_DEFAULT;
  }
};

export const getCardWrapperStyles = (
  version: string,
  theme: Theme,
  isNewPlpExpEnabled?: boolean
) => {
  const { spacing0, spacing20, spacing40, spacing80, spacing100 } =
    theme.spacing;
  const { surface30, textDecorative, textPrimary } = theme.colors;
  switch (version) {
    case PRODUCT_CARD_V2:
      return {
        padding: spacing40,
        paddingRight: spacing80,
        paddingLeft: spacing20,
        '&.odd': {
          paddingLeft: spacing80,
          paddingRight: spacing20,
        },
      };
    case PRODUCT_CARD_V3:
      return {
        padding: `${spacing0} ${spacing100} ${spacing0} ${spacing40}`,
        marginBottom: spacing80,
        '&.odd': {
          padding: `${spacing0} ${spacing40} ${spacing0} ${spacing100}`,
        },
      };
    default:
      if (isNewPlpExpEnabled) {
        return {
          borderBottom: `1px solid ${hexToRgb(textPrimary, 0.04)}`,
          '&.odd': {
            borderRight: `1px solid ${textDecorative}`,
          },
        };
      }
      return {
        borderBottom: `1px solid ${surface30}`,
        '&.odd': {
          borderRight: `1px solid ${surface30}`,
        },
      };
  }
};

export const hasTags = (product: ListingProduct | TipTile): boolean => {
  if (!isValidObject(product)) {
    return false;
  }
  if (isListingItemTipTile(product)) {
    return false;
  }
  const { newTags } = product || {};
  if (newTags?.length > 0) {
    return true;
  }
  return false;
};

export const showTagsByIndex = (
  index: number,
  productList: (ListingProduct | TipTile)[]
) => {
  const product = productList[index];
  if (hasTags(product)) {
    return true;
  }
  let adjProduct = productList[index + 1];
  if (index % 2 === 1) {
    adjProduct = productList[index - 1];
  }
  return hasTags(adjProduct);
};

export const getPidsSaleTemplate = (products: ListingProduct[]) => {
  const plpDifferentialPricingPIDs: [] = [];
  const plpNonDifferentialPricingPIDs: [] = [];
  products.forEach((product) => {
    const { productId, cohortSaleTemplateV2, cohortSaleTemplate } =
      product || {};
    if (cohortSaleTemplateV2 || cohortSaleTemplate) {
      const templateId =
        cohortSaleTemplateV2?.templateId || cohortSaleTemplate?.templateId;
      plpDifferentialPricingPIDs.push(`${productId}: ${templateId}`);
    } else {
      plpNonDifferentialPricingPIDs.push(productId);
    }
  });
  return { plpDifferentialPricingPIDs, plpNonDifferentialPricingPIDs };
};

/**
 * Adds tracking parameters to the given URL based on the site's navigation data.
 *
 * @param url - The URL to which tracking parameters should be added.
 * @returns The URL with added tracking parameters, or the original URL if an error occurs.
 */
export const addTrackingParams = (url: string) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    const { siteNavigation, siteSubNavigation } = window.datalayer || {};

    if (siteNavigation) {
      urlObj.searchParams.set(SITE_NAVIGATION, siteNavigation);
    }
    if (siteSubNavigation) {
      urlObj.searchParams.set(SITE_SUB_NAVIGATION, siteSubNavigation);
    }

    return urlObj.pathname + urlObj.search;
  } catch (error) {
    return url;
  }
};

export const shouldShowLeftTag = (
  hideProTag: boolean,
  leftTagText: string,
  isUserPro: boolean
) => !hideProTag || leftTagText !== PRO || isUserPro;

export const getTipTileDataForApps = ({
  appTrackingData,
  isLoggedIn,
  tipTileData,
  isConvSearch,
  gptConversationId,
  interactionInfo,
}: TrackingForAppParams) => {
  const { search_term, nykaa_store, source } = appTrackingData || {};

  const { location, tiptileClickCount } = interactionInfo || {};

  const { position, categoryName } = tipTileData || {};

  return {
    interaction_location: `Search:query:${search_term || ''}`,
    is_logged_in: !!isLoggedIn,
    listing_type: 'search',
    listing_value: search_term || '',
    nykaa_store: nykaa_store || '',
    plp_click_position: position,
    plp_interaction_value: location || plpInteractions.TIP_TILE,
    plp_tiptile_click_count: tiptileClickCount,
    search_keyword: search_term || '',
    source_platform: source || '',
    tiptile_name: categoryName,
    tiptile_position: position,
    has_nykaa_gpt_interaction: isConvSearch,
    gpt_conversation_id: gptConversationId,
  };
};
